"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const fmko_ts_common_1 = require("fmko-ts-common");
const KeyCode = tslib_1.__importStar(require("../util/KeyboardEventCodes"));
const DropdownUtil_1 = tslib_1.__importDefault(require("./DropdownUtil"));
const LabeledFieldWidget_1 = tslib_1.__importDefault(require("../widget/LabeledFieldWidget"));
const FieldSize_1 = require("../types/FieldSize");
const ImageWidget_1 = tslib_1.__importStar(require("../widget/ImageWidget"));
const Span_1 = tslib_1.__importDefault(require("../deprecated/elements/Span"));
/**
 * This class's WCAG is based on https://www.w3.org/WAI/ARIA/apg/example-index/combobox/combobox-select-only.html
 */
class AbstractDropdownWidget extends LabeledFieldWidget_1.default {
    constructor(args) {
        super({
            ...args,
            fieldType: "button"
        });
        this.options = [];
        this.optionElements = [];
        this.optionLiToIndexMap = new Map();
        this.optionToLiMap = new Map();
        this.noOptionsPlaceholder = "Der er ingen valgmuligheder";
        this.scrollListener = () => {
            this.renderTopOrBottom();
        };
        const { model, allowUnknownValues = false, options = new Map(), shouldSelectIfSingleOption = true } = args;
        this.model = model;
        this.allowUnknownValues = allowUnknownValues;
        this.shouldSelectIfSingleOption = shouldSelectIfSingleOption;
        this.setOptions(options);
    }
    getOptions() {
        return this.options;
    }
    getValue() {
        return this.value;
    }
    setValue(newValue, fireEvents = false) {
        const oldValue = this.value;
        const optionInList = this.options?.find(option => newValue === option
            || ((newValue && option) && this.model.isEqual(option, newValue)));
        const optionLi = this.optionToLiMap.get(optionInList);
        if (this.allowUnknownValues && newValue && !optionLi) {
            this.setUnknownValue(newValue, fireEvents);
            return;
        }
        this.value = optionInList;
        this.select(optionLi);
        if (fireEvents) {
            fmko_ts_common_1.ValueChangeEvent.fireIfNotEqual(this, oldValue, optionInList);
        }
        for (const anchoredButton of this.anchoredButtons) {
            anchoredButton.setEnabled(!!newValue);
        }
    }
    /* from Focusable interface. */
    setFocus(focused) {
        if (focused) {
            this.fieldElement.focus();
        }
        else {
            this.fieldElement.blur();
        }
    }
    setPlaceholder(value, overwriteRegularPlaceholder = false) {
        super.setPlaceholder(value);
        if (overwriteRegularPlaceholder) {
            this.regularPlaceholder = value;
        }
        if (this.getValue() !== undefined) {
            return;
        }
        this.fieldElement.textContent = value;
    }
    updateDropdownTextContent() {
        for (const option of this.options) {
            const optionLi = this.optionToLiMap.get(option);
            if (optionLi) {
                this.formatImage(option, optionLi);
                optionLi.textContent = this.model.formatItem(option);
            }
        }
        if (this.value) {
            this.setUnknownValue(this.value);
        }
    }
    createOptionLIElement({ option, isGroupElement = false, isUnknownOption = false }) {
        const elementId = fmko_ts_common_1.GlobalCounter.next();
        const optionElement = document.createElement("li");
        optionElement.id = elementId;
        this.formatImage(option, optionElement);
        let formatOption = isUnknownOption && this.model?.formatUnknownItem?.(option);
        if (!formatOption) {
            formatOption = this.model.formatItem(option);
        }
        if (isGroupElement) {
            const checkmarkImage = new ImageWidget_1.default({
                alt: "checked",
                imageDimension: ImageWidget_1.ImageDimensions.L,
                src: fmko_ts_common_1.ImageSrcWithVersion.getImage(fmko_ts_common_1.ImageSrc.CHECKMARK)
            });
            checkmarkImage.addCssClass("grouped-dropdown-checkmark");
            optionElement.appendChild(checkmarkImage.element);
        }
        const optionalLabel = new Span_1.default(formatOption);
        optionalLabel.addCssClass("option-label");
        optionElement.appendChild(optionalLabel.element);
        optionElement.setAttribute("role", fmko_ts_common_1.ARIARole.OPTION);
        optionElement.setAttribute(fmko_ts_common_1.ARIA.SELECTED, "false");
        optionElement.addEventListener("mouseover", event => {
            if (DropdownUtil_1.default.dateHasPassedThreshold(this.lastScrollDate)) {
                this.highlight(event.currentTarget);
            }
        });
        return optionElement;
    }
    createEmptyOptionLiElement(noOptionText) {
        const elementId = fmko_ts_common_1.GlobalCounter.next();
        const optionElement = document.createElement("li");
        optionElement.id = elementId;
        optionElement.classList.add("empty-option");
        const optionalLabel = new Span_1.default(noOptionText);
        optionalLabel.addCssClass("option-label");
        optionElement.appendChild(optionalLabel.element);
        optionElement.setAttribute("role", fmko_ts_common_1.ARIARole.OPTION);
        optionElement.setAttribute(fmko_ts_common_1.ARIA.SELECTED, "false");
        optionElement.addEventListener("mouseover", event => {
            if (DropdownUtil_1.default.dateHasPassedThreshold(this.lastScrollDate)) {
                this.highlight(event.currentTarget);
            }
        });
        optionElement.addEventListener("click", () => {
            this.clearValue(true);
            this.hideSuggestions();
        });
        return optionElement;
    }
    parseFieldElementValue(inputValue) {
        return this.getOptions().find(option => this.model.formatItem(option) === inputValue);
    }
    setFieldElementValue(value) {
        this.fieldElement.textContent = this.model.formatItem(value);
        const image = this.model.formatImage?.(value);
        if (image) {
            this.fieldElement.style.setProperty("--dropdown-field-bg-image", `url(${image.src})`);
            this.fieldElement.dataset.hasImage = "";
            this.fieldElement.setAttribute(fmko_ts_common_1.ARIA.LABEL, image.alt);
        }
        else {
            this.fieldElement.style.removeProperty("--dropdown-field-bg-image");
            delete this.fieldElement.dataset.hasImage;
            this.fieldElement.removeAttribute(fmko_ts_common_1.ARIA.LABEL);
        }
    }
    addButton(button) {
        if (this.lastButton) {
            fmko_ts_common_1.DOM.insertElementAfter(button.element, this.lastButton.element);
        }
        else {
            fmko_ts_common_1.DOM.insertElementAfter(button.element, this.fieldAndDropdownElement);
        }
        button.element.classList.add("left-anchored-element");
        this.anchoredButtons.push(button);
        this.lastButton = button;
    }
    decorateLabeledFieldWidgetElement(args) {
        const { placeholder = "Vælg en mulighed", fieldSizeClass } = args;
        this.anchoredButtons = [];
        this.element = document.createElement("div");
        this.element.classList.add("field-element-container", "flex-row");
        const ulId = fmko_ts_common_1.GlobalCounter.next();
        this.fieldElement.setAttribute(fmko_ts_common_1.ARIA.HAS_POPUP, fmko_ts_common_1.ARIARole.LIST_BOX);
        this.fieldElement.setAttribute(fmko_ts_common_1.ARIA.EXPANDED, "false");
        this.fieldElement.setAttribute(fmko_ts_common_1.ARIA.CONTROLS, ulId);
        this.fieldElement.setAttribute(fmko_ts_common_1.ARIA.LABELLED_BY, `${this.labelElement.id} ${this.fieldElement.id}`);
        this.regularPlaceholder = placeholder;
        this.setPlaceholder(this.noOptionsPlaceholder);
        this.fieldElement.classList.add("dropdown-button");
        this.dropdownList = document.createElement("ul");
        this.dropdownList.id = ulId;
        // We need to use mouseup and keyup for the button,
        // because for click enter is fired on keydown and keyup, while space is only fired on key up
        // and we the problem with keydown is that the dropdown gets focus, and when keyup is fired, the dropdown closes.
        this.fieldElement.addEventListener("mouseup", () => {
            this.handleDropdownButtonClick();
        });
        this.fieldElement.addEventListener("keyup", event => {
            if (this.isValidKeyupToOpenDropdown(event)) {
                this.handleDropdownButtonClick();
                this.handleDropDownKeyNavigation(event);
            }
        });
        this.dropdownList.addEventListener("mouseenter", () => {
            this.dropdownList.focus();
        });
        this.dropdownList.addEventListener("keydown", event => {
            this.handleDropDownKeyNavigation(event);
        }, true);
        this.dropdownList.addEventListener("keyup", event => {
            this.handleDropdownOnKeyUp(event);
        }, true);
        this.dropdownList.addEventListener("blur", () => {
            this.hideSuggestions();
            this.dropdownBlurDate = new Date();
        });
        fmko_ts_common_1.WCAG.addButtonEvent({
            ariaRole: fmko_ts_common_1.ARIARole.LIST_BOX,
            callback: () => {
                const option = this.getOptionFromLI(this.currentLiHighlighted);
                if (option !== undefined) {
                    this.hideSuggestions(false);
                    this.setValue(option, true);
                    if (!this.currentLiSelected) {
                        this.dropdownList.removeAttribute(fmko_ts_common_1.ARIA.ACTIVE_DESCENDANTS);
                    }
                }
            },
            element: this.dropdownList,
            tabIndex: "-1"
        });
        this.fieldAndDropdownElement = document.createElement("div");
        this.fieldAndDropdownElement.classList.add("dropdown-container");
        if (fieldSizeClass === FieldSize_1.FieldSize.MAX) {
            this.fieldAndDropdownElement.classList.add(fieldSizeClass);
        }
        this.fieldAndDropdownElement.appendChild(this.fieldElement);
        this.fieldAndDropdownElement.appendChild(this.dropdownList);
        this.add(this.fieldAndDropdownElement);
        this.fieldElementContainer = this.element;
        this.decorateAbstractDropdownElement(args);
    }
    highlight(newLi) {
        if (this.currentLiHighlighted) {
            this.currentLiHighlighted.classList.remove("dropdown-li-highlighted");
            this.currentLiHighlighted = undefined;
        }
        this.currentLiHighlighted = newLi;
        if (newLi) {
            this.dropdownList.setAttribute(fmko_ts_common_1.ARIA.ACTIVE_DESCENDANTS, newLi.id);
            newLi.classList.add("dropdown-li-highlighted");
        }
    }
    select(newLi) {
        this.fieldElement.classList.remove("dropdown-is-selected");
        if (!!this.currentLiSelected) {
            this.currentLiSelected.setAttribute(fmko_ts_common_1.ARIA.SELECTED, "false");
        }
        this.currentLiSelected = newLi;
        if (!this.currentLiSelected) {
            this.fieldElement.textContent = this.getPlaceholder() ?? "";
            this.highlight(undefined);
            return;
        }
        this.fieldElement.textContent = newLi.textContent;
        if (newLi.dataset.hasImage !== undefined && newLi.dataset.hasImage !== null) {
            this.fieldElement.style.setProperty("--dropdown-field-bg-image", newLi.style.getPropertyValue("--dropdown-field-bg-image"));
            this.fieldElement.dataset.hasImage = "";
            this.fieldElement.setAttribute(fmko_ts_common_1.ARIA.LABEL, newLi.getAttribute(fmko_ts_common_1.ARIA.LABEL));
        }
        else {
            this.fieldElement.style.removeProperty("--dropdown-field-bg-image");
            delete this.fieldElement.dataset.hasImage;
            this.fieldElement.removeAttribute(fmko_ts_common_1.ARIA.LABEL);
        }
        this.fieldElement.classList.add("dropdown-is-selected");
        newLi.setAttribute(fmko_ts_common_1.ARIA.SELECTED, "true");
    }
    showSuggestions() {
        this.fieldElement.setAttribute(fmko_ts_common_1.ARIA.EXPANDED, "true");
        this.dropdownList.setAttribute("tabindex", "0");
        this.renderTopOrBottom();
        this.dropdownList.classList.add("animation-ready");
        document.body.addEventListener("scroll", this.scrollListener, true);
        this.dropdownList.focus();
    }
    hideSuggestions(removeActiveDescendant = true) {
        this.dropdownList.setAttribute("tabindex", "-1");
        this.fieldElement.setAttribute(fmko_ts_common_1.ARIA.EXPANDED, "false");
        if (removeActiveDescendant && !this.currentLiSelected) {
            this.dropdownList.removeAttribute(fmko_ts_common_1.ARIA.ACTIVE_DESCENDANTS);
        }
        this.dropdownList.classList.remove("animation-ready");
        document.body.removeEventListener("scroll", this.scrollListener, true);
        this.fieldElement.focus();
    }
    handleDropDownKeyNavigation(event) {
        let element = undefined;
        if (!this.options.length) {
            return;
        }
        if (this.currentLiHighlighted == null) {
            const firstOption = this.optionToLiMap.get(this.options[0]);
            this.highlight(firstOption);
            return;
        }
        if (!KeyCode.hasModifierKey(event)) {
            event.preventDefault();
            if (KeyCode.isArrowDown(event)) {
                element = this.findEnabledAdjacentSibling(this.currentLiHighlighted, "next");
            }
            else if (KeyCode.isArrowUp(event)) {
                element = this.findEnabledAdjacentSibling(this.currentLiHighlighted, "previous");
            }
            else if (KeyCode.isHome(event)) {
                element = this.optionElements[0];
            }
            else if (KeyCode.isEnd(event)) {
                element = this.optionElements[this.optionElements.length - 1];
            }
            if (/^[a-zA-Z\dÆØÅæøå]/.test(event.key)) {
                const highlightedIndex = this.optionElements.indexOf(this.currentLiHighlighted);
                const endOfList = this.optionElements.slice(0, highlightedIndex + 1);
                const startOfList = this.optionElements.slice(highlightedIndex + 1); // Is empty is value it outside array
                for (const optionElement of [...startOfList, ...endOfList]) {
                    if (!optionElement.hasAttribute("disabled")) {
                        if (optionElement.textContent.substring(0, 1).toLowerCase() === event.key.toLowerCase()) {
                            element = optionElement;
                            break;
                        }
                    }
                }
            }
            if (element) {
                this.highlight(element);
                this.scrollIntoView(element);
            }
        }
    }
    handleDropdownButtonClick() {
        // Don't open listbox again if the event was closed by dropdown blur
        if (!DropdownUtil_1.default.dateHasPassedThreshold(this.dropdownBlurDate)) {
            return;
        }
        const nextExpandState = !(this.fieldElement.getAttribute(fmko_ts_common_1.ARIA.EXPANDED) === "true");
        if (this.options.length && nextExpandState) {
            if (!this.currentLiHighlighted) {
                this.currentLiHighlighted = this.optionElements[0];
            }
            // If there are options and the index hasn't been moved, then move it to 0
            const toHighlight = this.currentLiSelected !== undefined
                ? this.currentLiSelected
                : this.currentLiHighlighted;
            this.highlight(toHighlight);
            this.showSuggestions();
            this.scrollIntoView(toHighlight, false);
        }
        else {
            this.hideSuggestions();
        }
    }
    handleDropdownOnKeyUp(event) {
        if (!KeyCode.hasModifierKey(event)) {
            if (KeyCode.isEscape(event) || KeyCode.isTab(event)) {
                this.hideSuggestions();
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }
    renderTopOrBottom() {
        this.dropdownList.classList.remove("render-dropdown-on-top");
        const isDropdownFullyVisible = fmko_ts_common_1.DOM.isElementFullyVisible(this.dropdownList);
        if (!isDropdownFullyVisible) {
            this.dropdownList.classList.add("render-dropdown-on-top");
        }
    }
    scrollIntoView(element, updateLastScrollDate = true) {
        if (element) {
            const index = this.optionLiToIndexMap.get(element);
            if (typeof index === "number") {
                const previousSibling = element.previousElementSibling;
                if (previousSibling instanceof HTMLLIElement
                    && previousSibling.getAttribute("role") === fmko_ts_common_1.ARIARole.PRESENTATION) {
                    element = previousSibling;
                }
                element.scrollIntoView({
                    block: "nearest"
                });
                // FMKGUI-329, Det Første element i dropdowns bliver ikke fokuseret. Hvis man går ned på det for hurtigt
                if (updateLastScrollDate) {
                    this.lastScrollDate = new Date();
                }
            }
        }
    }
    findEnabledAdjacentSibling(element, direction, originalElement) {
        if (element === originalElement) {
            return originalElement; // We have iterated through the entire list of children
        }
        const relevantOriginalElement = originalElement || element;
        const adjacentIndex = direction === "previous" ? -1 : 1;
        const optionIndex = this.optionElements.findIndex(optionElement => optionElement === element);
        const nextIndex = optionIndex + adjacentIndex;
        let adjacentElement;
        if (nextIndex > -1) {
            // Either the element is next in the array, or we are at this.optionElements.length, thus we have to reset to 0
            adjacentElement = this.optionElements[nextIndex % this.optionElements.length];
        }
        else {
            // If we are -1, then we need to go to the end again
            adjacentElement = this.optionElements[this.optionElements.length - 1];
        }
        return this.getElementOrFindAdjacent(direction, adjacentElement, relevantOriginalElement);
    }
    getElementOrFindAdjacent(direction, element, originalElement) {
        if (element) {
            if (!element.hasAttribute("disabled")) {
                return element;
            }
            else {
                // If it's the first recursion then add element as original element
                this.findEnabledAdjacentSibling(element, direction, originalElement);
            }
        }
    }
    getOptionFromLI(element) {
        const index = this.optionElements.findIndex(optionLI => optionLI === element);
        return this.options[index];
    }
    formatImage(value, element) {
        const image = this.model.formatImage?.(value);
        if (image) {
            element.style.setProperty("--dropdown-field-bg-image", `url(${image.src})`);
            element.dataset.hasImage = "";
            element.setAttribute(fmko_ts_common_1.ARIA.LABEL, image.alt);
        }
        else {
            element.style.removeProperty("--dropdown-field-bg-image");
            delete element.dataset.hasImage;
            element.removeAttribute(fmko_ts_common_1.ARIA.LABEL);
        }
    }
    isValidKeyupToOpenDropdown(event) {
        if (KeyCode.hasModifierKey(event)) {
            return false;
        }
        if (KeyCode.isModifierKey(event)) {
            return false;
        }
        if (KeyCode.isTab(event)) {
            return false;
        }
        return true;
    }
}
exports.default = AbstractDropdownWidget;
//# sourceMappingURL=AbstractDropdownWidget.js.map